/**
 * 指挥页立体山体（DEM 网格）
 *
 * Mapbox GL 2.15 没有 v3 的 terrainShadow；立体靠 raster-dem 把地面撑起来。
 * 高德/谷歌底图是 GCJ-02，全球 DEM 是 WGS84，必须换纠偏后的高程源，
 * 否则山脊会偏几十到几百米，看起来像峰谷对调。
 */

import { isDashboardEarthDismissed } from './earthSpinGate';
import { getMapboxToken } from '../mapboxToken';
import { getMapCoordAdapter, getDatumProfile } from './basemapCoord';
import { GCJ_DEM_TILE_TEMPLATE, installGcjDemFetchHook } from './gcjDemTiles';

export const TERRAIN_3D_STORAGE_KEY = 'dashboard_terrain_3d';
export const TERRAIN_DEM_SOURCE_ID = 'zhxt-mapbox-dem';
export const TERRAIN_SKY_LAYER_ID = 'zhxt-terrain-sky';
export const TERRAIN_HILLSHADE_LAYER_ID = 'zhxt-terrain-hillshade';
export const TERRAIN_DEFAULT_EXAGGERATION = 1.35;

function isEarthOverviewZoom(map) {
  try {
    const z = Number(map?.getZoom?.());
    return Number.isFinite(z) && z <= 2.5;
  } catch (_) {
    return false;
  }
}

export function isTerrain3dWanted(
  storage = typeof localStorage !== 'undefined' ? localStorage : null
) {
  if (typeof window !== 'undefined' && window.__zhxtTerrain3dWanted != null) {
    return Boolean(window.__zhxtTerrain3dWanted);
  }
  if (!storage) return false;
  try {
    return storage.getItem(TERRAIN_3D_STORAGE_KEY) === '1';
  } catch (_) {
    return false;
  }
}

export function setTerrain3dWanted(
  on,
  storage = typeof localStorage !== 'undefined' ? localStorage : null
) {
  const wanted = Boolean(on);
  if (typeof window !== 'undefined') window.__zhxtTerrain3dWanted = wanted;
  if (!storage) return;
  try {
    storage.setItem(TERRAIN_3D_STORAGE_KEY, wanted ? '1' : '0');
  } catch (_) {
    /* ignore */
  }
}

/** 切样式时的立体默认：混合开，卫星/二维关。 */
export function defaultTerrain3dForStyle(styleMode) {
  return styleMode === 'hybrid';
}

function wantedDemDatum(map) {
  return getMapCoordAdapter(map).datum;
}

function listDemSourceSpecs(datum) {
  const token = getMapboxToken();
  const specs = [];
  if (getDatumProfile(datum).warpDem) {
    installGcjDemFetchHook();
    specs.push({
      label: 'mapbox-terrain-rgb-gcj',
      spec: {
        type: 'raster-dem',
        tiles: [GCJ_DEM_TILE_TEMPLATE],
        tileSize: 256,
        maxzoom: 14,
        encoding: 'mapbox'
      }
    });
    return specs;
  }
  // 显式 pngraw：比 mapbox:// 更不易被 GL 内部 tileset 解析失败
  if (token) {
    specs.push({
      label: 'mapbox-terrain-rgb',
      spec: {
        type: 'raster-dem',
        tiles: [
          `https://api.mapbox.com/v4/mapbox.terrain-rgb/{z}/{x}/{y}.pngraw?access_token=${token}`
        ],
        tileSize: 256,
        maxzoom: 14,
        encoding: 'mapbox'
      }
    });
    specs.push({
      label: 'mapbox-dem-v1',
      spec: {
        type: 'raster-dem',
        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
        tileSize: 512,
        maxzoom: 14
      }
    });
  }
  specs.push({
    label: 'mapzen-terrarium',
    spec: {
      type: 'raster-dem',
      tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
      tileSize: 256,
      maxzoom: 15,
      encoding: 'terrarium'
    }
  });
  return specs;
}

function detachDemSource(map) {
  if (!map) return;
  try {
    map.setTerrain?.(null);
  } catch (_) {
    /* ignore */
  }
  try {
    if (map.getLayer?.(TERRAIN_HILLSHADE_LAYER_ID)) {
      map.removeLayer(TERRAIN_HILLSHADE_LAYER_ID);
    }
  } catch (_) {
    /* ignore */
  }
  try {
    if (map.getSource?.(TERRAIN_DEM_SOURCE_ID)) {
      map.removeSource(TERRAIN_DEM_SOURCE_ID);
    }
  } catch (_) {
    /* ignore */
  }
  map.__zhxtDemLabel = null;
  map.__zhxtDemDatum = null;
}

function ensureDemSource(map) {
  if (!map) return false;
  const datum = wantedDemDatum(map);
  try {
    if (map.getSource?.(TERRAIN_DEM_SOURCE_ID) && map.__zhxtDemDatum === datum) {
      return true;
    }
  } catch (_) {
    /* continue */
  }
  if (map.getSource?.(TERRAIN_DEM_SOURCE_ID)) {
    detachDemSource(map);
  }
  const specs = listDemSourceSpecs(datum);
  for (const item of specs) {
    try {
      map.addSource(TERRAIN_DEM_SOURCE_ID, item.spec);
      map.__zhxtDemLabel = item.label;
      map.__zhxtDemDatum = datum;
      console.info('[terrain3d] DEM 源已添加', item.label, datum);
      return true;
    } catch (err) {
      console.warn('[terrain3d] DEM 源失败，换下一个', item.label, err?.message || err);
      try {
        if (map.getSource?.(TERRAIN_DEM_SOURCE_ID)) {
          map.removeSource(TERRAIN_DEM_SOURCE_ID);
        }
      } catch (_) {
        /* ignore */
      }
    }
  }
  return false;
}

function ensureHillshadeLayer(map) {
  // 默认不加 hillshade：灰绿高光会把卫星图涂成橡皮泥。立体靠 DEM 网格本身。
  if (!map) return;
  try {
    if (map.getLayer?.(TERRAIN_HILLSHADE_LAYER_ID)) {
      map.removeLayer(TERRAIN_HILLSHADE_LAYER_ID);
    }
  } catch (_) {
    /* ignore */
  }
}

function ensureSkyLayer(map) {
  if (!map?.addLayer) return;
  try {
    if (map.getLayer?.(TERRAIN_SKY_LAYER_ID)) return;
  } catch (_) {
    /* continue */
  }
  try {
    map.addLayer({
      id: TERRAIN_SKY_LAYER_ID,
      type: 'sky',
      paint: {
        'sky-type': 'atmosphere',
        'sky-atmosphere-sun': [0.0, 80.0],
        'sky-atmosphere-sun-intensity': 8
      }
    });
  } catch (err) {
    console.warn('[terrain3d] sky 添加失败', err?.message || err);
  }
}

function applyTerrainLight(map) {
  try {
    // 弱光：避免把卫星贴图洗成假灰色。立体主要靠几何起伏，不靠假山影。
    map.setLight?.({
      anchor: 'map',
      color: '#ffffff',
      intensity: 0.12,
      position: [1.5, 210, 40]
    });
  } catch (_) {
    /* ignore */
  }
}

function attachTerrainDebug(map) {
  if (typeof window === 'undefined' || !map) return;
  window.__zhxtDebugTerrain = () => {
    const c = map.getCenter?.();
    let elev = null;
    try {
      elev = map.queryTerrainElevation?.([c.lng, c.lat], { exaggerated: true });
    } catch (_) {
      /* ignore */
    }
    const info = {
      terrain: map.getTerrain?.() || null,
      demLabel: map.__zhxtDemLabel || null,
      demDatum: map.__zhxtDemDatum || wantedDemDatum(map),
      warpDem: getMapCoordAdapter(map).warpDem,
      basemapId: getMapCoordAdapter(map).basemapId,
      hasDemSource: Boolean(map.getSource?.(TERRAIN_DEM_SOURCE_ID)),
      hasHillshade: Boolean(map.getLayer?.(TERRAIN_HILLSHADE_LAYER_ID)),
      pitch: map.getPitch?.(),
      zoom: map.getZoom?.(),
      elevation: elev,
      projection: map.getProjection?.()
    };
    console.info('[terrain3d] debug', info);
    return info;
  };
}

function whenDemReady(map, callback) {
  let finished = false;
  const done = (reason) => {
    if (finished) return;
    finished = true;
    try {
      map.off?.('sourcedata', onData);
    } catch (_) {
      /* ignore */
    }
    callback(reason);
  };
  const onData = (e) => {
    if (e?.sourceId !== TERRAIN_DEM_SOURCE_ID) return;
    try {
      if (map.isSourceLoaded?.(TERRAIN_DEM_SOURCE_ID)) done('loaded');
    } catch (_) {
      /* ignore */
    }
  };
  try {
    if (map.isSourceLoaded?.(TERRAIN_DEM_SOURCE_ID)) {
      done('already');
      return;
    }
  } catch (_) {
    /* ignore */
  }
  try {
    map.on('sourcedata', onData);
  } catch (_) {
    done('nosignal');
    return;
  }
  const waitMs = getDatumProfile(wantedDemDatum(map)).warpDem ? 8000 : 2800;
  setTimeout(() => done('timeout'), waitMs);
}

function applyTerrainMesh(map, exaggeration) {
  try {
    map.setMaxPitch?.(85);
  } catch (_) {
    /* ignore */
  }
  try {
    map.setTerrain({
      source: TERRAIN_DEM_SOURCE_ID,
      exaggeration
    });
  } catch (err) {
    console.warn('[terrain3d] setTerrain 失败', err?.message || err);
    return false;
  }
  ensureHillshadeLayer(map);
  ensureSkyLayer(map);
  applyTerrainLight(map);
  attachTerrainDebug(map);
  return true;
}

/**
 * 开启立体山体。地球全景中默认拒绝（force 除外）。
 * 不改相机：便于同一倾斜角度开关对比。
 * @returns {boolean} 是否已提交开启（高程瓦片仍可能稍后才到）
 */
export function enableMapTerrain3d(map, options = {}) {
  if (!map) return false;
  if (!options.force && isEarthOverviewZoom(map) && !isDashboardEarthDismissed()) {
    return false;
  }
  if (!ensureDemSource(map)) {
    console.warn('[terrain3d] 无法添加 DEM 源');
    return false;
  }
  const exaggeration = Number.isFinite(Number(options.exaggeration))
    ? Number(options.exaggeration)
    : TERRAIN_DEFAULT_EXAGGERATION;

  const meshOk = applyTerrainMesh(map, exaggeration);
  if (!meshOk) return false;

  // 不改 pitch/zoom：开关对比必须同一视角；用户自己右键倾斜即可

  whenDemReady(map, (reason) => {
    applyTerrainMesh(map, exaggeration);
    let elev = null;
    try {
      const c = map.getCenter?.();
      if (c) elev = map.queryTerrainElevation?.([c.lng, c.lat], { exaggerated: true });
    } catch (_) {
      /* ignore */
    }
    console.info('[terrain3d] DEM 就绪', { reason, elev, dem: map.__zhxtDemLabel });
    if (typeof window !== 'undefined') window.__zhxtTerrainElev = elev;
    if (typeof options.onReady === 'function') {
      options.onReady({
        reason,
        elevation: elev,
        ok: Number.isFinite(Number(elev))
      });
    }
  });
  return true;
}

export function disableMapTerrain3d(map) {
  if (!map) return;
  detachDemSource(map);
  try {
    if (map.getLayer?.(TERRAIN_SKY_LAYER_ID)) map.removeLayer(TERRAIN_SKY_LAYER_ID);
  } catch (_) {
    /* ignore */
  }
}

/** 切底图或关 GLB 后：按立体开关恢复山体，不改等高线等叠加。 */
export function restoreMapTerrain3d(map, options = {}) {
  if (!map || !isTerrain3dWanted()) return false;
  if (isEarthOverviewZoom(map) && !options.force) return false;
  return enableMapTerrain3d(map, {
    exaggeration: options.exaggeration,
    force: options.force
  });
}
